import type {
  CashVarianceActionDto,
  CashVarianceCaseDto,
  CashExpenseCategoryDto,
  CashExpenseDto,
  CashFundingSourceCategoryDto,
  CashLocationDto,
  CashMovementDto,
  CashOperatingConfigurationDto,
  CashRemittanceDestinationDto,
  CashScopeModeStateDto,
  CashCountedDenominationSnapshotDto,
  CashDenominationBaselineType,
  CashDenominationConfidence,
  CashDenominationInventoryCheckpointApplicationStatus,
  CashDenominationInventoryCheckpointInventoryStatus,
  CashDenominationInventoryCheckpointOrigin,
  CashDenominationInventoryCheckpointSource,
  CashDenominationInventoryCheckpointSourceEventSide,
  CashDenominationInventoryCheckpointSourceEventType,
  CashDenominationSource,
  CashLocationType,
  CashSessionDto,
  CashTreasuryDistributionRequestDto,
  CashTreasuryDistributionRequestStatus,
  CashTransferDto,
} from '../cash/index.js';
import type { CashSyncErrorCode } from './cash-sync-error-codes.js';
import {
  cashTargetStateSyncEntityTypeValues,
  type CashTargetStateSyncEntityType,
  type CashTargetStateSyncPayloadDto,
} from './cash-target-state-sync.dto.js';

export interface CashSyncRemittanceDestinationDto extends CashRemittanceDestinationDto {
  revision: number;
}

export interface CashSyncConfigItems {
  cashOperatingConfiguration: CashSyncOperatingConfigurationDto;
  cashScopeModeStates: CashSyncScopeModeStateDto[];
  cashRemittanceDestinations: CashSyncRemittanceDestinationDto[];
  cashLifecycleEvents: CashTargetStateSyncPayloadDto[];
  cashLocations: CashLocationDto[];
  expenseCategories: CashExpenseCategoryDto[];
  fundingSourceCategories: CashFundingSourceCategoryDto[];
}

export interface CashSyncOperatingConfigurationDto extends CashOperatingConfigurationDto {
  id: string;
  updatedByOperationId: string;
  createdAt: string;
}

export interface CashSyncScopeModeStateDto extends CashScopeModeStateDto {
  id: string;
  activeActivationId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CashSyncConfigPullRequest {
  cursor?: string | null;
}

export interface CashSyncConfigPullResponse {
  items: CashSyncConfigItems;
  nextCursor: string | null;
  serverTime: string;
}

export const cashSyncEntityTypeValues = [
  'cashSession',
  'cashMovement',
  'cashTransfer',
  'cashExpense',
  'cashVarianceCase',
  'cashVarianceAction',
  'cashDenominationSnapshot',
  'cashDenominationAllocationRevision',
  'cashDenominationBaseline',
  'cashDenominationInventoryCheckpoint',
  ...cashTargetStateSyncEntityTypeValues,
] as const;
export type CashSyncEntityType = (typeof cashSyncEntityTypeValues)[number];

export const cashTreasuryDistributionRequestSyncEventTypeValues = [
  'cashTreasuryDistributionRequestRequested',
  'cashTreasuryDistributionRequestAccepted',
  'cashTreasuryDistributionRequestDeclined',
  'cashTreasuryDistributionRequestCancelled',
  'cashTreasuryDistributionRequestExpired',
] as const;
export type CashTreasuryDistributionRequestSyncEventType =
  (typeof cashTreasuryDistributionRequestSyncEventTypeValues)[number];

export const cashTreasuryFundingSyncEventTypeValues = ['cashTreasuryFundingCreated'] as const;
export type CashTreasuryFundingSyncEventType =
  (typeof cashTreasuryFundingSyncEventTypeValues)[number];

export const cashTreasuryExpenseSyncEventTypeValues = [
  'cashTreasuryExpenseCreated',
  'cashTreasuryExpenseVoided',
] as const;
export type CashTreasuryExpenseSyncEventType =
  (typeof cashTreasuryExpenseSyncEventTypeValues)[number];

export interface CashSyncPushItemBase {
  entityType: CashSyncEntityType;
  entityId: string;
  occurredAt: string;
  stationId?: string | null;
}

export interface CashSyncSessionDto extends CashSessionDto {
  closeRequiresDenominations?: boolean | null;
}

export interface CashSyncPushSessionItem extends CashSyncPushItemBase {
  entityType: 'cashSession';
  payload: CashSyncSessionDto;
}

export interface CashSyncPushMovementItem extends CashSyncPushItemBase {
  entityType: 'cashMovement';
  payload: CashMovementDto;
}

export interface CashSyncPushTransferItem extends CashSyncPushItemBase {
  entityType: 'cashTransfer';
  payload: CashTransferDto;
}

export interface CashSyncPushExpenseItem extends CashSyncPushItemBase {
  entityType: 'cashExpense';
  payload: CashExpenseDto;
}

export interface CashSyncPushVarianceCaseItem extends CashSyncPushItemBase {
  entityType: 'cashVarianceCase';
  payload: CashVarianceCaseDto;
}

export interface CashSyncPushVarianceActionItem extends CashSyncPushItemBase {
  entityType: 'cashVarianceAction';
  payload: CashVarianceActionDto;
}

export interface CashSyncPushDenominationSnapshotItem extends CashSyncPushItemBase {
  entityType: 'cashDenominationSnapshot';
  payload: CashCountedDenominationSnapshotDto;
}

export interface CashSyncDenominationRowDto {
  denom: number;
  count: number;
  rowTotalMinor: number;
}

export interface CashSyncDenominationAllocationRevisionDto {
  id: string;
  companyId: string;
  allocationId: string;
  cashMovementId: string;
  cashLocationId: string;
  cashSessionId: string | null;
  storeId: string | null;
  locationId: string | null;
  stationId: string | null;
  currentRevisionId: string | null;
  movementAmountMinor: number;
  movementOccurredAt: string;
  source: CashDenominationSource;
  confidence: CashDenominationConfidence;
  denominationTotalMinor: number;
  effectiveAt: string;
  note: string | null;
  createdByUserId: string | null;
  createdAt: string;
  allocationCreatedAt: string;
  allocationUpdatedAt: string;
  rows: CashSyncDenominationRowDto[];
}

export interface CashSyncPushDenominationAllocationRevisionItem extends CashSyncPushItemBase {
  entityType: 'cashDenominationAllocationRevision';
  payload: CashSyncDenominationAllocationRevisionDto;
}

export interface CashSyncDenominationBaselineDto {
  id: string;
  companyId: string;
  storeId: string | null;
  locationId: string | null;
  stationId: string | null;
  cashLocationId: string;
  cashSessionId: string | null;
  sourceSnapshotId: string | null;
  sourceCashSessionId: string | null;
  baselineType: CashDenominationBaselineType;
  expectedAmountMinor: number;
  countedAmountMinor: number;
  varianceAmountMinor: number;
  source: Extract<CashDenominationSource, 'countedBaseline'>;
  confidence: Extract<CashDenominationConfidence, 'countedBaseline'>;
  countedAt: string;
  effectiveAt: string;
  supersededThrough: string | null;
  varianceCaseId: string | null;
  varianceRecognitionMovementId: string | null;
  note: string | null;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  rows: CashSyncDenominationRowDto[];
}

export interface CashSyncPushDenominationBaselineItem extends CashSyncPushItemBase {
  entityType: 'cashDenominationBaseline';
  payload: CashSyncDenominationBaselineDto;
}

export interface CashSyncDenominationInventoryCheckpointRowDto {
  denom: number;
  count: number;
  rowTotalMinor: number;
}

export interface CashSyncDenominationInventoryCheckpointPublicationDto {
  id: string;
  companyId: string;
  storeId: string | null;
  locationId: string | null;
  stationId: string | null;
  cashLocationId: string;
  cashLocationType: CashLocationType;
  inventoryStatus: CashDenominationInventoryCheckpointInventoryStatus;
  source: CashDenominationInventoryCheckpointSource;
  sourceEventType: CashDenominationInventoryCheckpointSourceEventType;
  sourceEventId: string;
  sourceEventSide: CashDenominationInventoryCheckpointSourceEventSide;
  sourceEventOccurredAt: string;
  asOf: string;
  rows: CashSyncDenominationInventoryCheckpointRowDto[] | null;
  totalMinor: number | null;
  ledgerBalanceMinor: number;
  ledgerMovementCount: number | null;
  ledgerLastMovementAt: string | null;
  ledgerLastMovementUpdatedAt: string | null;
  expectedLedgerBalanceMinor: number | null;
  differenceMinor: number | null;
  cashSessionId: string | null;
  lastMovementId: string | null;
  sourceSnapshotId: string | null;
  sourceBaselineId: string | null;
}

export interface CashSyncTreasuryInventoryCheckpointPayloadDto
  extends CashSyncDenominationInventoryCheckpointPublicationDto {
  origin: Extract<CashDenominationInventoryCheckpointOrigin, 'cloudTreasuryCount'>;
  applicationStatus: Extract<CashDenominationInventoryCheckpointApplicationStatus, 'accepted'>;
  publishedAt: string;
  updatedAt: string;
}

export interface CashSyncTreasuryInventoryCheckpointStateItem {
  entityType: 'cashDenominationInventoryCheckpoint';
  entityId: string;
  occurredAt: string;
  payload: CashSyncTreasuryInventoryCheckpointPayloadDto;
}

export interface CashSyncPushDenominationInventoryCheckpointItem extends CashSyncPushItemBase {
  entityType: 'cashDenominationInventoryCheckpoint';
  payload: CashSyncDenominationInventoryCheckpointPublicationDto;
}

export interface CashSyncPushTargetStateItem extends CashSyncPushItemBase {
  entityType: CashTargetStateSyncEntityType;
  eventType: CashTargetStateSyncPayloadDto['eventType'];
  payload: CashTargetStateSyncPayloadDto;
}

export type CashSyncPushItem =
  | CashSyncPushSessionItem
  | CashSyncPushMovementItem
  | CashSyncPushTransferItem
  | CashSyncPushExpenseItem
  | CashSyncPushVarianceCaseItem
  | CashSyncPushVarianceActionItem
  | CashSyncPushDenominationSnapshotItem
  | CashSyncPushDenominationAllocationRevisionItem
  | CashSyncPushDenominationBaselineItem
  | CashSyncPushDenominationInventoryCheckpointItem
  | CashSyncPushTargetStateItem;

export interface CashSyncPushRequest {
  cursor?: string | null;
  batch: CashSyncPushItem[];
}

export interface CashSyncEntityIdentity {
  entityType: CashSyncEntityType;
  entityId: string;
}

export interface CashSyncPushFailure extends CashSyncEntityIdentity {
  errorCode: CashSyncErrorCode;
  message?: string;
  details?: CashSyncPushFailureDetails;
}

export interface CashSyncPushResponse {
  acked: CashSyncEntityIdentity[];
  failed: CashSyncPushFailure[];
}

export interface CashSyncTreasuryDistributionRequestStateItem {
  entityType: 'cashTreasuryDistributionRequest';
  entityId: string;
  eventType: CashTreasuryDistributionRequestSyncEventType;
  status: CashTreasuryDistributionRequestStatus;
  stationId: string;
  occurredAt: string;
  payload: CashTreasuryDistributionRequestDto;
}

export interface CashSyncTreasuryDistributionRequestsPullRequest {
  cursor?: string | null;
  stationId: string;
}

export interface CashSyncTreasuryDistributionRequestsPullResponse {
  items: CashSyncTreasuryDistributionRequestStateItem[];
  nextCursor: string | null;
  serverTime: string;
}

/**
 * Durable mutation identity carried with Cloud-authored Cash state.
 *
 * The envelope is intentionally complete: a downstream replica must be able to
 * prove the operation, its authority scope, and its durable result without
 * inferring any of those fields from the result row alone.
 */
export interface CashSyncOperationEnvelopeDto {
  operationId: string;
  requestHash: string;
  companyId: string;
  scopeType: 'location' | 'treasury';
  scopeId: string;
  actorUserId: string;
  resultEntityType: string;
  resultEntityId: string;
  occurredAt: string;
  createdAt: string;
}

export interface CashSyncTreasuryFundingPayloadDto {
  cashMovement: CashMovementDto;
  operation: CashSyncOperationEnvelopeDto;
  denominationAllocationRevision?: CashSyncDenominationAllocationRevisionDto | null;
}

export interface CashSyncTreasuryFundingStateItem {
  entityType: 'cashTreasuryFunding';
  entityId: string;
  eventType: CashTreasuryFundingSyncEventType;
  occurredAt: string;
  payload: CashSyncTreasuryFundingPayloadDto;
}

export interface CashSyncTreasuryFundingsPullRequest {
  cursor?: string | null;
}

export interface CashSyncTreasuryFundingsPullResponse {
  items: CashSyncTreasuryFundingStateItem[];
  nextCursor: string | null;
  serverTime: string;
}

export interface CashSyncTreasuryExpenseResultDto {
  cashExpense: CashExpenseDto;
  cashMovement: CashMovementDto;
  reversalMovement?: CashMovementDto | null;
  denominationAllocationRevision?: CashSyncDenominationAllocationRevisionDto | null;
  reversalDenominationAllocationRevision?: CashSyncDenominationAllocationRevisionDto | null;
  creationOperation: CashSyncOperationEnvelopeDto;
  transitionOperation?: CashSyncOperationEnvelopeDto | null;
}

export interface CashSyncTreasuryExpensePayloadDto extends CashSyncTreasuryExpenseResultDto {
  /** Complete root-to-direct-predecessor chain for a replacement expense. */
  predecessorResults: CashSyncTreasuryExpenseResultDto[];
}

export interface CashSyncTreasuryExpenseStateItem {
  entityType: 'cashTreasuryExpense';
  entityId: string;
  eventType: CashTreasuryExpenseSyncEventType;
  occurredAt: string;
  payload: CashSyncTreasuryExpensePayloadDto;
}

export interface CashSyncTreasuryExpensesPullRequest {
  cursor?: string | null;
}

export interface CashSyncTreasuryExpensesPullResponse {
  items: CashSyncTreasuryExpenseStateItem[];
  nextCursor: string | null;
  serverTime: string;
}

export interface CashSyncTreasuryInventoryCheckpointsPullRequest {
  cursor?: string | null;
}

export interface CashSyncTreasuryInventoryCheckpointsPullResponse {
  items: CashSyncTreasuryInventoryCheckpointStateItem[];
  nextCursor: string | null;
  serverTime: string;
}

export const cashSyncFailureCategoryValues = [
  'missing_prerequisite',
  'inactive_prerequisite',
  'missing_relation',
  'invalid_payload',
  'invalid_station_context',
  'policy_blocked',
  'auth_required',
  'cloud_unreachable',
  'upstream_error',
  'unexpected_error',
] as const;
export type CashSyncFailureCategory = (typeof cashSyncFailureCategoryValues)[number];

export interface CashSyncPushFailureDetails {
  failureCategory: CashSyncFailureCategory;
  invalidActorUserIds?: string[];
  prerequisiteType?:
    | 'cash_location'
    | 'cash_operating_configuration'
    | 'cash_expense_category'
    | 'cash_funding_source_category'
    | 'cash_session'
    | 'cash_variance_case'
    | 'cash_variance_action'
    | 'cash_expense'
    | 'cash_transfer'
    | 'cash_movement'
    | 'purchase_committed'
    | 'purchase_cancellation'
    | 'cash_custody_receipt'
    | 'cash_treasury_distribution_request'
    | 'cash_remittance_destination'
    | 'cash_denomination_snapshot'
    | 'cash_denomination_correction'
    | 'cash_denomination_allocation'
    | 'cash_denomination_allocation_revision';
  prerequisiteId?: string | null;
  replayableAfterFix?: boolean;
  upstreamStatus?: number;
  upstreamCode?: string;
}
